<template>
  <ComponentDrawer :title="t('canvasUI.canvasBuilder.canvasDrawerTheme.setTheme')" @update:show="onDrawerStateChange">
    <div class="flex flex-col gap-5 h-full">
      <template v-if="launchDarkly.enable_themes">
        <div v-if="!isBlock" class="c-form-item">
          <NFormItem :label="t('canvasUI.canvasBuilder.canvasDrawerTheme.appearance')" :show-feedback="false">
            <NCheckbox v-model:checked="isFluid" :disabled="isSaving">
              {{ t('canvasUI.canvasBuilder.canvasDrawerTheme.isFluid') }}
            </NCheckbox>
          </NFormItem>
          <NFormItem
            v-if="isFluid"
            :label="t('canvasUI.canvasBuilder.canvasDrawerTheme.aspectRatio')"
            :show-feedback="false"
          >
            <NSelect v-model:value="aspectRatio" :disabled="isSaving" :options="aspectRatioOptions" />
          </NFormItem>

          <CPdfDimensionsSelector
            v-if="showDimensionsSelector"
            class="my-2"
            :default-height="defaultPdfHeight"
            :default-width="defaultPdfWidth"
            :disabled="isSaving"
            :height="pdfDimensionsHeight"
            :preset="pdfDimensionsPreset"
            :show-ratio-warning="isPdfRatioDifferentFromDefault"
            :width="pdfDimensionsWidth"
            @update:height="pdfDimensionsHeight = $event"
            @update:preset="pdfDimensionsPreset = $event"
            @update:width="pdfDimensionsWidth = $event"
          />

          <CLabelWithHelpText
            class="font-size-[16px]"
            :label="t('canvasUI.canvasBuilder.canvasDrawerTheme.backgroundImageLabel')"
          >
            <template #info>
              {{ t('canvasUI.canvasBuilder.canvasDrawerTheme.backgroundImageDescription') }}
            </template>
          </CLabelWithHelpText>
          <CSingleFileSelector
            :file="
              backgroundImage ? { thumbnail_url: backgroundImage ?? '', content_type: FileContentTypeEnum.IMAGE } : null
            "
            :hide-content-type="true"
            :selected-page-index="0"
            size="small"
            upload-Button-class="w-full"
            @clearSelectedFile="() => (backgroundImage = '')"
            @openContentSelector="chooseContent"
          />
        </div>
        <div
          v-if="launchDarkly.enable_background_image_theme_options && backgroundImage && !isBlock"
          class="c-form-item"
        >
          <CBackgroundImageThemeOptions
            :background-attachment="backgroundAttachment"
            :background-image="backgroundImage"
            :background-position-x="backgroundPositionX"
            :background-position-y="backgroundPositionY"
            :background-repeat="backgroundRepeat"
            :background-size="backgroundSize"
            :hide-image-selector="true"
            @bg-property-change="handleBackgroundPropertyChange"
          />
        </div>

        <div v-if="!isBlock" class="c-form-item">
          <CLabelWithHelpText
            class="font-size-[16px]"
            :label="t('canvasUI.canvasBuilder.canvasDrawerTheme.backgroundVideoLabel')"
          >
            <template #info>
              {{ t('canvasUI.canvasBuilder.canvasDrawerTheme.backgroundVideoDescription') }}
            </template>
          </CLabelWithHelpText>
          <CInput
            v-model:value="backgroundVideoUrl"
            :placeholder="t('canvasUI.canvasBuilder.canvasDrawerTheme.backgroundVideoLabel')"
            :style="{ '--n-color': 'transparent' }"
          />
        </div>

        <NFormItem :label="t('canvasUI.canvasBuilder.canvasDrawerTheme.themes')" :show-feedback="false">
          <div class="flex flex-wrap gap-3 w-full">
            <NCheckbox
              v-if="isTemplate && launchDarkly.enable_maintain_section_theme_control"
              v-model:checked="maintainSectionTheme"
              :disabled="isSaving"
              @update:checked="handleMaintainSectionThemeChange"
            >
              {{ t('canvasUI.canvasBuilder.canvasDrawerTheme.maintainSectionTheme') }}
            </NCheckbox>

            <NCheckbox
              v-if="isSection && launchDarkly.enable_maintain_section_theme_control"
              v-model:checked="inheritThemeFromParent"
              :disabled="isSaving"
            >
              {{ t('canvasUI.canvasBuilder.canvasDrawerTheme.inheritThemeFromParent') }}
            </NCheckbox>

            <template v-if="!fetchingThemes">
              <div
                v-if="!themes?.length"
                class="flex items-center justify-center w-full py-4 text-center bg-primary6 rounded-2"
              >
                <CIcon class="mr-2" icon="info-circle" />
                <span>{{ t('canvasUI.canvasBuilder.canvasDrawerTheme.noThemesAvailable') }}</span>
              </div>

              <div
                v-if="isCanvas && templateTheme && templateTheme.name !== activeTheme?.name"
                class="w-full flex flex-wrap items-center gap-1"
              >
                <span class="cursor-pointer underline color-primary" @click="revertToTemplateTheme(templateTheme)">
                  {{ t('canvasUI.canvasBuilder.canvasDrawerTheme.revertToTemplateTheme') }}
                </span>
                <CHelpText>
                  {{
                    t('canvasUI.canvasBuilder.canvasDrawerTheme.revertToTemplateThemeDescription', {
                      themeName: templateTheme.name,
                    })
                  }}
                </CHelpText>
              </div>
              <div
                v-for="theme in themes"
                :key="theme.id"
                class="relative rounded-2 pa-2 border-solid cursor-pointer w-[calc(50%-6px)]"
                :class="{ 'bg-primary5': activeTheme?.id === theme.id }"
                :style="{ borderColor: `${themeVars.primary5}!important` }"
                @click="onThemeSelect(theme)"
              >
                <CThemePreview
                  class="rounded-1 pa-2 mb-1"
                  :height="90"
                  placement="center-left"
                  responsive
                  :text-size="16"
                  :theme="theme"
                  :title-size="28"
                />
                <CIcon
                  v-if="theme.is_default"
                  class="absolute top-5 right-5 opacity-50"
                  :color="getContrastTextColor(theme.settings?.background_color ?? '')"
                  fa-type="fa-kit"
                  icon="regular-palette-circle-check"
                  size="14"
                />
                <div v-tooltip="theme.name" class="w-full truncate">
                  <span class="text-sm font-bold">{{ theme.name }}</span>
                </div>
              </div>
            </template>
            <div v-else class="w-full text-center">
              <NSpin :show="true" />
            </div>
          </div>
        </NFormItem>
      </template>
      <template v-else-if="!isBlock">
        <NFormItem :label="t('canvasUI.canvasBuilder.canvasDrawerTheme.appearance')" :show-feedback="false">
          <NCheckbox v-model:checked="isFluid" :disabled="isSaving">
            {{ t('canvasUI.canvasBuilder.canvasDrawerTheme.isFluid') }}
          </NCheckbox>
        </NFormItem>
        <NFormItem
          v-if="isFluid"
          :label="t('canvasUI.canvasBuilder.canvasDrawerTheme.aspectRatio')"
          :show-feedback="false"
        >
          <NSelect v-model:value="aspectRatio" :disabled="isSaving" :options="aspectRatioOptions" />
        </NFormItem>

        <CPdfDimensionsSelector
          v-if="showDimensionsSelector"
          class="my-2"
          :default-height="defaultPdfHeight"
          :default-width="defaultPdfWidth"
          :disabled="isSaving"
          :height="pdfDimensionsHeight"
          :preset="pdfDimensionsPreset"
          :show-ratio-warning="isPdfRatioDifferentFromDefault"
          :width="pdfDimensionsWidth"
          @update:height="pdfDimensionsHeight = $event"
          @update:preset="pdfDimensionsPreset = $event"
          @update:width="pdfDimensionsWidth = $event"
        />

        <NFormItem :label="t('canvasUI.canvasBuilder.canvasDrawerTheme.backgroundColor')" :show-feedback="false">
          <CSwatchSelector v-if="enableCanvasTokens" v-model="canvasBgColor" :colors="palette" include-transparent />
          <NColorPicker v-else v-model:value="canvasBgColor" :disabled="isSaving" />
        </NFormItem>

        <div class="c-form-item">
          <CLabelWithHelpText
            class="font-size-[16px]"
            :label="t('canvasUI.canvasBuilder.canvasDrawerTheme.backgroundImageLabel')"
          >
            <template #info>
              {{ t('canvasUI.canvasBuilder.canvasDrawerTheme.backgroundImageDescription') }}
            </template>
          </CLabelWithHelpText>
          <CSingleFileSelector
            :file="
              backgroundImage ? { thumbnail_url: backgroundImage ?? '', content_type: FileContentTypeEnum.IMAGE } : null
            "
            :hide-content-type="true"
            :selected-page-index="0"
            size="small"
            upload-Button-class="w-full"
            @clearSelectedFile="() => (backgroundImage = '')"
            @openContentSelector="chooseContent"
          />
        </div>
        <div v-if="launchDarkly.enable_background_image_theme_options && backgroundImage" class="c-form-item">
          <CBackgroundImageThemeOptions
            :background-attachment="backgroundAttachment"
            :background-image="backgroundImage"
            :background-position-x="backgroundPositionX"
            :background-position-y="backgroundPositionY"
            :background-repeat="backgroundRepeat"
            :background-size="backgroundSize"
            :hide-image-selector="true"
            @bg-property-change="handleBackgroundPropertyChange"
          />
        </div>

        <div class="c-form-item">
          <CLabelWithHelpText
            class="font-size-[16px]"
            :label="t('canvasUI.canvasBuilder.canvasDrawerTheme.backgroundVideoLabel')"
          >
            <template #info>
              {{ t('canvasUI.canvasBuilder.canvasDrawerTheme.backgroundVideoDescription') }}
            </template>
          </CLabelWithHelpText>
          <CInput
            v-model:value="backgroundVideoUrl"
            :placeholder="t('canvasUI.canvasBuilder.canvasDrawerTheme.backgroundVideoLabel')"
            :style="{ '--n-color': 'transparent' }"
          />
        </div>

        <template v-if="mode === CanvasBuilderMode.ADMIN">
          <div class="c-form-item">
            <CLabelWithHelpText
              class="font-size-[16px]"
              :label="t('canvasUI.canvasBuilder.canvasDrawerTheme.defaultFont')"
            >
              <template v-if="!!launchDarkly.enable_canvas_tokens" #info>
                {{ t('canvasUI.canvasBuilder.canvasDrawerTheme.defaultFontDescription') }}
              </template>
            </CLabelWithHelpText>
            <NSelect
              :disabled="isSaving"
              :on-update:value="handleValueUpdate"
              :options="fontOptions"
              :render-label="renderOptionLabel"
              :value="font"
            />
          </div>

          <NFormItem :label="t('canvasUI.canvasBuilder.canvasDrawerTheme.fontSample', { font })" :show-feedback="false">
            <div :style="{ fontFamily: font, fontSize: '24px' }">
              Aa Bb Cc Dd Ee Ff Gg Hh Ii Jj Kk Ll Mm Nn Oo Pp Qq Rr Ss Tt Uu Vv Ww Xx Yy Zz
            </div>
          </NFormItem>

          <NFormItem v-if="showFontUploader" :label="t('canvasUI.canvasBuilder.canvasDrawerTheme.uploadFont')">
            <NUpload
              :accept="SUPPORTED_FONT_EXTENSIONS.join(',')"
              :default-upload="false"
              :disabled="isSaving"
              :max="1"
              :multiple="false"
              :on-update:file-list="onFontSelectForUpload"
            >
              <NUploadDragger>
                <div>
                  <CIcon icon="upload" size="26px" />
                </div>
                <div class="mt-3">{{ t('canvasUI.canvasBuilder.multimediaSettings.uploadHint') }}</div>
              </NUploadDragger>
            </NUpload>
          </NFormItem>
          <span v-if="fileExists" class="font-xs text-center" :style="{ color: themeVars.error }">
            {{ t('canvasUI.canvasBuilder.multimediaSettings.fontAlreadyExists') }}
          </span>
        </template>

        <NFormItem
          :label="`${t('canvasUI.canvasBuilder.canvasDrawerTheme.spacing.title')}: ${globalComponentSpacing}px`"
          :show-feedback="false"
        >
          <div class="flex flex-col w-full">
            <CSlider
              :disabled="isSaving"
              :format-tooltip="(value: number) => `${value}px`"
              :step="DEFAULT_GLOBAL_COMPONENT_SPACING_INTERVAL"
              :value="globalComponentSpacing"
              @update:value="(value: number) => (globalComponentSpacing = value)"
            >
              <template #helpText>
                <span
                  class="underline cursor-pointer"
                  :style="{
                    color: themeVars.primary,
                  }"
                  @click="resetAllComponentSpacing"
                  >{{ t('canvasUI.common.reset') }}
                </span>
                <span>&nbsp;</span>
                <span>{{ t('canvasUI.canvasBuilder.canvasDrawerTheme.spacing.helpText') }}</span>
              </template>
            </CSlider>
          </div>
        </NFormItem>

        <CCollapse v-if="showAdvancedThemingOptions" :default-expanded-names="['palette']" size="small">
          <CCollapseItem
            class="collapse-item"
            :name="'palette'"
            :title="`${t('canvasUI.canvasBuilder.canvasDrawerTheme.colorPalette')} (${palette.length})`"
          >
            <CPaletteConfigurator
              :initial-palette="palette"
              @update:palette="(newPalette: string[]) => (palette = newPalette)"
            />
          </CCollapseItem>
          <CCollapseItem
            class="collapse-item"
            :name="'typography'"
            :title="`${t('canvasUI.canvasBuilder.canvasDrawerTheme.typography')}  (${Object.keys(typography).length})`"
          >
            <CTypographyConfigurator
              v-model:configuration="typography"
              :font-options="fontOptions.filter((f) => !f.action)"
              :palette="palette"
            />
          </CCollapseItem>
        </CCollapse>
      </template>
      <CButton
        v-if="debuggable"
        class="w-11 h-11"
        :disabled="isSaving"
        type="primary"
        @click.stop="isInspectingTheme = !isInspectingTheme"
      >
        <template #icon>
          <CIcon icon="display-code" size="16" />
        </template>
      </CButton>

      <CJsonEditor
        v-if="isInspectingTheme"
        :object="activeCanvas ? activeCanvas?.theme || {} : {}"
        :teleport-to-body="false"
        @close="isInspectingTheme = false"
        @update="updateThemeFromJsonEditor"
      />

      <hr class="block border-transparent mt-12" />

      <div
        class="absolute bottom-0 left-0 z-2 mx-8 py-4"
        :style="{
          width: 'calc(100% - 2 * var(--n-body-padding))',
          backgroundColor: themeVars.base,
        }"
      >
        <CButton
          block
          class="mt-auto"
          :disabled="fileExists"
          :loading="isSaving"
          type="primary"
          @click="() => handleSave()"
        >
          {{ t('canvasUI.common.apply') }}
        </CButton>
      </div>
    </div>
  </ComponentDrawer>
</template>

<script setup lang="ts">
import {
  NFormItem,
  NColorPicker,
  NSelect,
  NUpload,
  NUploadDragger,
  NCheckbox,
  NSpin,
  type UploadFileInfo,
} from 'naive-ui'
import { onMounted, ref, inject, h, computed, type ComputedRef, type Ref } from 'vue'
import { useI18n } from 'vue-i18n'
import useThemeVars from '@lib/composables/useThemeVars'
import { cloneDeep, isEqual, omit, orderBy } from 'lodash-es'
import type { AxiosInstance } from 'axios'

import CBackgroundImageThemeOptions from '@lib/components/CBackgroundImageThemeOptions/CBackgroundImageThemeOptions.vue'
import CButton from '@lib/components/CButton/CButton.vue'
import CCollapse from '@lib/components/CCollapse/CCollapse.vue'
import CCollapseItem from '@lib/components/CCollapse/CCollapseItem.vue'
import CHelpText from '@lib/components/CHelpText/CHelpText.vue'
import CIcon from '@lib/components/CIcon/CIcon.vue'
import CInput from '@lib/components/CInput/CInput.vue'
import CJsonEditor from '@lib/components/CJsonEditor/CJsonEditor.vue'
import CLabelWithHelpText from '@lib/components/CLabelWithHelpText/CLabelWithHelpText.vue'
import ComponentDrawer from '@canvas-builder/components/container/ComponentDrawer/ComponentDrawer.vue'
import CPaletteConfigurator from '@lib/components/CPaletteConfigurator/CPaletteConfigurator.vue'
import CPdfDimensionsSelector from '@lib/components/CPdfDimensionsSelector/CPdfDimensionsSelector.vue'
import CSingleFileSelector from '@lib/components/CSingleFileSelector/CSingleFileSelector.vue'
import CSlider from '@lib/components/CSlider/CSlider.vue'
import CSwatchSelector from '@lib/components/CSwatchSelector/CSwatchSelector.vue'
import CThemePreview from '@lib/components/CThemePreview/CThemePreview.vue'
import CTypographyConfigurator from '@lib/components/CTypographyConfigurator/CTypographyConfigurator.vue'

import useCanvasTheme from '@lib/apps/canvas-builder/composables/useCanvasTheme'
import useCanvas from '@lib/apps/canvas-builder/composables/useCanvas'
import useCanvasHistory from '@canvas-builder/composables/useCanvasHistory'
import useCanvasNodes from '@canvas-builder/composables/useCanvasNodes'
import { applyFont } from '@lib/util/font.util'
import { insertItemSorted } from '@lib/util/array'
import {
  CanvasBuilderMode,
  type CanvasPdfDimensions,
  type CanvasTheme,
  type ComponentNode,
} from '@canvas-builder/types/canvas'
import { type FileRetrieve, type CanvasRetrieve, AccessTypeEnum, FileContentTypeEnum } from '@openapi'
import { getFileNameWithoutExtension, waitUntilFileViewable } from '@lib/apps/canvas-builder/util/file.util'
import { DEFAULT_FONT, aspectRatios } from '@canvas-builder/constants'
import { type PitcherInfo } from '@lib/types/app'
import { SUPPORTED_FONT_EXTENSIONS } from '@lib/constants/uploads.const'
import { applyToTreeBy } from '@canvas-builder/util/tree'
import type { LaunchDarklyEnv } from '@lib/types/launchDarkly.types'
import type {
  BackgroundAttachment,
  BackgroundPositionX,
  BackgroundPositionY,
  BackgroundRepeat,
  BackgroundSize,
} from '@lib/types/background'
import type { UiSelectContentRequest, UiSelectContentResponse } from '@sdk/api/modules/ui/types.ui'
import {
  DEFAULT_GLOBAL_COMPONENT_SPACING,
  DEFAULT_GLOBAL_COMPONENT_SPACING_INTERVAL,
} from '@canvas-builder/constants/theme.const'
import type { SelectOptionActionUrl } from '@lib/theme/theme.types'
import { CanvasThemeStatus, type CanvasThemeRetrieve } from '@lib/types/canvasTheme'
import { getContrastTextColor } from '@lib/util/color'
import { GOTENBERG_DIMENSION_PATTERN } from '@lib/constants/dimensions.const'

interface Props {
  baseStackingContext?: number
}
defineProps<Props>()
const launchDarkly = inject<ComputedRef<LaunchDarklyEnv>>(
  'launchDarkly',
  computed(() => ({})),
)

const pitcherInfo = inject<ComputedRef<PitcherInfo>>(
  'pitcherInfo',
  computed(() => ({
    accessToken: '',
    instanceId: '',
    apiOrigin: '',
    isAnyTypeOfAdmin: false,
  })),
)

const canvasesThemesAssigner =
  inject<
    (payload: {
      canvas_id: CanvasRetrieve['id']
      theme_id: CanvasThemeRetrieve['id']
    }) => Promise<{ id: CanvasThemeRetrieve['id']; canvas_id: CanvasRetrieve['id'] }>
  >('canvasesThemesAssigner')
const canvasThemeUnassigner = inject<(payload: { canvas_id: CanvasRetrieve['id'] }) => void>('canvasThemeUnassigner')
const themesFetcher = inject<() => Promise<CanvasThemeRetrieve[]>>('themesFetcher', async () => [])
const canvasThemeFetcher =
  inject<(params: { canvas_id: CanvasRetrieve['id'] }) => Promise<CanvasThemeRetrieve>>('canvasThemeFetcher')
const restApiAxios = inject<Ref<AxiosInstance | undefined>>('restApiAxios', ref())
const selectContent =
  inject<(payload?: UiSelectContentRequest) => Promise<UiSelectContentResponse>>('selectUserContent')

const {
  activeCanvas,
  areTokenConstraintsActive,
  canvasContent,
  isAdmin,
  isBlock,
  isCanvas,
  isTemplate,
  isSection,
  save,
  updateActiveCanvasProperties,
  mode,
  updateMultimediaNodeWithThemeAsset,
  areThemeAssetsEnabled,
  componentNodesById,
  updateNodeDataById,
  pitcherSettings,
} = useCanvas()
const { resetCanvasHistory } = useCanvasHistory()
const { updateComponentsWithThemeAssets } = useCanvasNodes()
const {
  activeTheme,
  applyTheme,
  isEditingCanvasTheme,
  canvasBgColor,
  clearSectionThemes,
  customFonts,
  globalComponentSpacing,
  font,
  inheritThemeFromParent,
  isFluid,
  aspectRatio,
  maintainSectionTheme,
  palette,
  logo,
  assets,
  typography,
  backgroundAttachment,
  backgroundImage,
  backgroundVideoUrl,
  backgroundPositionX,
  backgroundPositionY,
  backgroundSize,
  backgroundRepeat,
  pdfDimensionsPreset,
  pdfDimensionsWidth,
  pdfDimensionsHeight,
  getDefaultTypographyConfiguration,
  loadSectionThemes,
  loadSectionThemesAssets,
  originalTheme,
  setFontAndFetchAsset,
  themes,
  updateThemeProperties,
  updateTextCSSVariables,
  removeCssUrlFunc,
  setBackgroundImageProperties,
} = useCanvasTheme()

const revertThemeId = ref<CanvasThemeRetrieve['id'] | null>(null)
const templateTheme = ref<CanvasThemeRetrieve | null>(null)
const fetchingThemes = ref(false)
const fontOptions = ref<SelectOptionActionUrl[]>([])
const selectedFont = ref<{ file: File; name: string }>()
const uploadedFonts = ref<{ name: string; url?: string | null }[]>([])
const fileExists = ref(false)
const isInspectingTheme = ref(false)
const isSaving = ref(false)
const showFontUploader = ref(false)
const { t } = useI18n()
const themeVars = useThemeVars()

const debuggable = computed(() => !!launchDarkly.value?.enable_canvas_node_debugging)
const enableCanvasTokens = computed(() => !!launchDarkly.value.enable_canvas_tokens && areTokenConstraintsActive.value)
const aspectRatioOptions = computed(() =>
  aspectRatios.map((ratio) => ({
    label: getRatioLabel(ratio),
    value: ratio,
  })),
)

function getRatioLabel(ratio: string) {
  switch (ratio) {
    case 'unlocked':
      return t('canvasUI.canvasBuilder.canvasDrawerTheme.unlocked')
    case 'contain':
      return t('canvasUI.canvasBuilder.canvasDrawerTheme.contain')
    default:
      return ratio
  }
}

const showAdvancedThemingOptions = computed(
  () =>
    !!launchDarkly.value?.enable_canvas_tokens &&
    isAdmin.value &&
    activeCanvas.value?.is_template &&
    !activeCanvas.value?.is_section,
)

const defaultPdfWidth = computed(() => {
  const width = pitcherSettings.value?.default_pdf_page_dimensions?.width
  if (!width) return undefined
  return typeof width === 'number' ? `${width}px` : width
})

const defaultPdfHeight = computed(() => {
  const height = pitcherSettings.value?.default_pdf_page_dimensions?.height
  if (!height) return undefined
  return typeof height === 'number' ? `${height}px` : height
})

const showDimensionsSelector = computed(
  () =>
    launchDarkly.value.enable_scale_content &&
    launchDarkly.value.enable_canvas_pdf_dimensions &&
    !isBlock.value &&
    (isCanvas.value || isTemplate.value),
)

const isPdfRatioDifferentFromDefault = computed(() => {
  if (!defaultPdfWidth.value || !defaultPdfHeight.value || !pdfDimensionsWidth.value || !pdfDimensionsHeight.value) {
    return false
  }

  // Extract numeric value from string, handling Gotenberg-supported units (mm, cm, in, pt, pc, px) or plain numbers
  const parseValue = (val: string | number) => {
    if (typeof val === 'number') return val
    // Remove any Gotenberg unit suffix and parse the numeric value
    return parseFloat(val.replace(new RegExp(GOTENBERG_DIMENSION_PATTERN, 'g'), ''))
  }

  const defaultW = parseValue(defaultPdfWidth.value)
  const defaultH = parseValue(defaultPdfHeight.value)
  const customW = parseValue(pdfDimensionsWidth.value)
  const customH = parseValue(pdfDimensionsHeight.value)

  if (
    !defaultW ||
    !defaultH ||
    !customW ||
    !customH ||
    defaultW === 0 ||
    defaultH === 0 ||
    customW === 0 ||
    customH === 0
  ) {
    return false
  }

  const defaultRatio = defaultW / defaultH
  const customRatio = customW / customH

  return Math.abs(defaultRatio - customRatio) > 0.01
})

const hasPendingChanges = computed(
  () =>
    font.value !== activeCanvas.value?.theme?.font?.name ||
    canvasBgColor.value !== activeCanvas.value?.theme?.canvas_bg_color ||
    backgroundImage.value !== removeCssUrlFunc(activeCanvas.value?.theme?.style?.backgroundImage || '') ||
    backgroundVideoUrl.value !== activeCanvas.value?.theme?.background_video_url ||
    globalComponentSpacing.value !== activeCanvas.value?.theme?.global_component_spacing ||
    inheritThemeFromParent.value !== activeCanvas.value?.theme?.inherit_theme_from_parent ||
    isFluid.value !== activeCanvas.value?.theme?.fluid ||
    aspectRatio.value !== (activeCanvas.value?.theme?.aspect_ratio || 'unlocked') ||
    maintainSectionTheme.value !== activeCanvas.value?.theme?.maintain_section_theme ||
    !isEqual(palette.value, activeCanvas.value?.theme?.palette) ||
    !isEqual(typography.value, activeCanvas.value?.theme?.typography) ||
    !isEqual(logo.value, activeCanvas.value?.theme?.logo) ||
    !isEqual(assets.value, activeCanvas.value?.theme?.assets) ||
    !isEqual(
      pdfDimensionsPreset.value
        ? {
            preset: pdfDimensionsPreset.value,
            width: pdfDimensionsWidth.value,
            height: pdfDimensionsHeight.value,
          }
        : undefined,
      activeCanvas.value?.theme?.pdf_dimensions,
    ),
)

function checkIfFileExists(fileName: string | undefined): boolean {
  if (!fileName) return false

  const name = getFileNameWithoutExtension(fileName)
  return uploadedFonts.value.some((item) => item.name === name)
}

async function chooseContent() {
  if (!selectContent) {
    console.error('selectContent is not provided')
    return
  }

  try {
    const data = await selectContent({
      allowed_selector_types: ['assets', 'external_links', 'ai_images', 'pexels_images', 'theme'],
      max_selections: 1,
      allowed_asset_content_types: [FileContentTypeEnum.IMAGE],
    })

    const newImageUrl = data?.content?.[0]?.thumbnail_url ?? (data?.content?.[0] as any)?.content_url

    if (newImageUrl) {
      backgroundImage.value = newImageUrl
    }
  } catch (error) {
    console.error('Error in chooseContent:', error)
  }
}

function onFontSelectForUpload(fileList: UploadFileInfo[]) {
  const file = fileList?.[0]?.file

  fileExists.value = checkIfFileExists(file?.name)
  if (fileExists.value) return false

  if (!file) {
    selectedFont.value = undefined
    return
  }
  const fontFamilyName = file.name
    .replace(/\.[^.]*$/, '')
    .replace(/\s+/g, '-')
    .replace(/[^\w-]+/g, '')

  selectedFont.value = { file, name: fontFamilyName }
  const reader = new FileReader()
  reader.onload = (e: ProgressEvent<FileReader>) => {
    const fontDataUrl = e.target ? e.target.result : ''

    applyFont(fontDataUrl, fontFamilyName)

    // Update the font-family
    !fontOptions.value.find((f) => f.value === fontFamilyName) &&
      insertItemSorted(fontOptions.value, { label: fontFamilyName, value: fontFamilyName }, (a: any, b: any) =>
        a.value.localeCompare(b.value),
      )

    font.value = fontFamilyName
  }
  reader.readAsDataURL(file)
}

function resetAllComponentSpacing() {
  const newCanvasContent = canvasContent.value.slice()

  applyToTreeBy(
    newCanvasContent,
    (node: ComponentNode) => {
      if (node.style) node.style = omit(node.style, 'marginBottom')
    },
    (node) => !!node.style,
  )

  canvasContent.value = newCanvasContent
}

function immediatelyApplyThemeAssets(theme: CanvasThemeRetrieve) {
  if (!theme) return

  try {
    // Check if feature flags are properly enabled
    if (!areThemeAssetsEnabled.value) {
      console.warn('Theme assets are disabled - skipping theme asset application')
      return
    }

    // Clear any existing section themes to start fresh
    if (activeCanvas.value?.sections?.length) {
      clearSectionThemes(activeCanvas.value.sections)
    }

    // Apply theme based on maintainSectionTheme setting
    if (maintainSectionTheme.value) {
      // If maintaining section themes:
      // 1. First apply the canvas theme to all components
      updateComponentsWithThemeAssets(
        theme,
        componentNodesById.value as Record<string, ComponentNode>,
        updateNodeDataById,
        activeCanvas.value,
      )

      // 2. Then apply section-specific themes if needed
      if (activeCanvas.value?.sections?.length) {
        loadSectionThemes(activeCanvas.value.sections)
        loadSectionThemesAssets(activeCanvas.value.sections, updateMultimediaNodeWithThemeAsset, undefined)
      }
    } else {
      // If not maintaining section themes, apply canvas theme to everything
      updateComponentsWithThemeAssets(
        theme,
        componentNodesById.value as Record<string, ComponentNode>,
        updateNodeDataById,
        activeCanvas.value,
      )
    }
  } catch (e) {
    console.error('Error in immediatelyApplyThemeAssets:', e)
  }
}

function revertToTemplateTheme(theme: CanvasThemeRetrieve) {
  // store the existing theme id to be deleted
  revertThemeId.value = activeTheme.value?.id ?? null
  applyTheme(theme)
  immediatelyApplyThemeAssets(theme)
}

async function uploadFont() {
  if (!pitcherInfo.value) throw new Error('Pitcher info is not provided - CanvasDrawerTheme.vue')
  if (!selectedFont.value) throw new Error('Font is not selected')
  if (!restApiAxios.value) throw new Error('Rest API Axios is not provided')

  const formData = new FormData()
  formData.append('content', selectedFont.value.file)
  formData.append('name', selectedFont.value.name)
  formData.append('instance_id', pitcherInfo.value.instanceId)
  formData.append('access_type', AccessTypeEnum.PUBLIC)
  formData.append('is_asset', 'true')

  return await restApiAxios.value.post<unknown, FileRetrieve>('files/', formData)
}

const renderOptionLabel = (option: SelectOptionActionUrl) => {
  const addItem = option.action === 'add'
  return h('div', { class: 'select-option', style: `${addItem ? 'text-align: center;' : null}` }, [
    addItem ? h(CIcon, { icon: 'plus' }, { default: () => 'd' }) : null,
    h('span', { style: `${addItem ? 'margin-left: 8px;' : null} ` }, option.label),
  ])
}

async function updateThemeFromJsonEditor(theme: CanvasTheme) {
  if (!theme) return
  await handleSave(theme)
}

async function handleSave(theme?: CanvasTheme) {
  try {
    if (!pitcherInfo.value) throw new Error('Pitcher info is not provided - CanvasDrawerTheme.vue on save')
    if (!activeCanvas.value) throw new Error('Canvas is not there. Something is wrong')
    if (!hasPendingChanges.value && !theme) {
      isEditingCanvasTheme.value = false
      return
    }
    isSaving.value = true
    let canvasFont: CanvasTheme['font']
    if (selectedFont.value) {
      // new font
      let fontUploadResponse = await uploadFont()

      if (!fontUploadResponse.content_url)
        fontUploadResponse = await waitUntilFileViewable(fontUploadResponse.id, pitcherInfo.value)
      canvasFont = {
        url: fontUploadResponse.content_url!,
        name: selectedFont.value.name,
      }
    } else {
      // existing font
      const f = fontOptions.value.find((item) => item.value === font.value)
      canvasFont = f ? { name: f.value, url: f.url as string } : { name: DEFAULT_FONT, url: '' }
    }

    // If not maintaining section themes, clear them first to ensure we start fresh
    if (!maintainSectionTheme.value && activeCanvas.value?.sections?.length) {
      clearSectionThemes(activeCanvas.value.sections)
    }

    // Create the new theme object
    const newTheme = theme
      ? theme
      : ({
          ...activeCanvas.value?.theme,
          style:
            activeCanvas.value?.theme?.style || backgroundImage.value
              ? {
                  ...activeCanvas.value?.theme?.style,
                  ...(backgroundImage.value
                    ? {
                        backgroundAttachment: backgroundAttachment.value,
                        backgroundImage: `url(${backgroundImage.value})`,
                        backgroundPositionX: backgroundPositionX.value,
                        backgroundPositionY: backgroundPositionY.value,
                        backgroundRepeat: backgroundRepeat.value,
                        backgroundSize: backgroundSize.value,
                      }
                    : {
                        backgroundAttachment: undefined,
                        backgroundImage: undefined,
                        backgroundPositionX: undefined,
                        backgroundPositionY: undefined,
                        backgroundRepeat: undefined,
                        backgroundSize: undefined,
                      }),
                }
              : undefined,
          background_video_url: backgroundVideoUrl.value || undefined,
          font: canvasFont,
          canvas_bg_color: canvasBgColor.value,
          global_component_spacing: globalComponentSpacing.value,
          fluid: isFluid.value,
          aspect_ratio: aspectRatio.value,
          maintain_section_theme: maintainSectionTheme.value,
          inherit_theme_from_parent: inheritThemeFromParent.value,
          palette: palette.value,
          typography: typography.value,
          logo: logo.value,
          assets: assets.value,
          pdf_dimensions: pdfDimensionsPreset.value
            ? {
                preset: pdfDimensionsPreset.value as CanvasPdfDimensions['preset'],
                width: pdfDimensionsWidth.value,
                height: pdfDimensionsHeight.value,
              }
            : undefined,
        } satisfies CanvasTheme)

    // First save just the theme without touching content
    const c = await save(activeCanvas.value?.id, {
      theme: newTheme,
      fields: 'theme',
    })
    if (c) {
      if (launchDarkly.value.enable_themes && activeTheme.value?.id) {
        if (revertThemeId.value) {
          await canvasThemeUnassigner?.({
            canvas_id: activeCanvas.value.id,
          })
          revertThemeId.value = null
        } else {
          await canvasesThemesAssigner?.({
            theme_id: activeTheme.value.id,
            canvas_id: activeCanvas.value.id,
          })
        }

        originalTheme.value = cloneDeep(activeTheme.value)
      }

      // Update the canvas with theme properties
      updateActiveCanvasProperties(c)
      c.theme && updateThemeProperties(c.theme)

      // If not maintaining section themes, apply chosen theme to all components
      if (!maintainSectionTheme.value && activeTheme.value) {
        updateComponentsWithThemeAssets(
          activeTheme.value,
          componentNodesById.value as Record<string, ComponentNode>,
          updateNodeDataById,
          activeCanvas.value,
        )
      }
    }
    isSaving.value = false

    resetCanvasHistory()
    isEditingCanvasTheme.value = false
  } catch (e) {
    isSaving.value = false
  }
}

function onDrawerStateChange(show: boolean) {
  if (isSaving.value) return
  if (!show) {
    setBackgroundImageProperties(activeCanvas.value?.theme?.style)
    font.value = activeCanvas.value?.theme?.font?.name || DEFAULT_FONT
    canvasBgColor.value = activeCanvas.value?.theme?.canvas_bg_color || ''
    globalComponentSpacing.value =
      activeCanvas.value?.theme?.global_component_spacing || DEFAULT_GLOBAL_COMPONENT_SPACING
    isFluid.value = activeCanvas.value?.theme?.fluid ?? false
    aspectRatio.value = activeCanvas.value?.theme?.aspect_ratio || 'unlocked'
    maintainSectionTheme.value = activeCanvas.value?.theme?.maintain_section_theme ?? true
    inheritThemeFromParent.value = activeCanvas.value?.theme?.inherit_theme_from_parent ?? false
    palette.value = activeCanvas.value?.theme?.palette?.length
      ? activeCanvas.value?.theme?.palette
      : [themeVars.value.primary]
    typography.value = activeCanvas.value?.theme?.typography || getDefaultTypographyConfiguration()
    logo.value = activeCanvas.value?.theme?.logo ?? null
    assets.value = activeCanvas.value?.theme?.assets ?? []
    pdfDimensionsPreset.value = activeCanvas.value?.theme?.pdf_dimensions?.preset
    pdfDimensionsWidth.value = activeCanvas.value?.theme?.pdf_dimensions?.width
    pdfDimensionsHeight.value = activeCanvas.value?.theme?.pdf_dimensions?.height
    updateTextCSSVariables(typography.value)
    isEditingCanvasTheme.value = false
    if (launchDarkly.value.enable_themes) {
      applyTheme(originalTheme.value)
      if (originalTheme.value) {
        // Apply theme assets to ensure changes are visible immediately
        try {
          // First trigger an update to canvas content multimedia elements
          updateComponentsWithThemeAssets(
            originalTheme.value,
            componentNodesById.value as Record<string, ComponentNode>,
            updateNodeDataById,
            activeCanvas.value,
          )

          // Then update section elements if maintaining section themes
          if (maintainSectionTheme.value && activeCanvas.value?.sections?.length) {
            loadSectionThemes(activeCanvas.value.sections)
            loadSectionThemesAssets(activeCanvas.value.sections, updateMultimediaNodeWithThemeAsset, undefined)
          }
        } catch (e) {
          console.error('Error applying theme when closing drawer:', e)
        }
      }
    }
  }
}

async function handleValueUpdate(value: string, option: SelectOptionActionUrl) {
  if (option?.action === 'add') {
    showFontUploader.value = true
  } else {
    await setFontAndFetchAsset(value, option.url as string)
    font.value = value
  }
}

function handleMaintainSectionThemeChange(value: boolean) {
  if (!activeCanvas.value || !activeTheme.value) return

  try {
    // Force a deep update to ensure all changes are reflected
    activeCanvas.value = { ...activeCanvas.value }

    if (value) {
      // Changed to true: Restore section themes and their assets
      if (activeCanvas.value.sections?.length) {
        // First clear any existing section styles
        clearSectionThemes(activeCanvas.value.sections)
        // Then load the proper themes
        loadSectionThemes(activeCanvas.value.sections)

        // Only load theme assets if the feature is enabled
        if (areThemeAssetsEnabled.value) {
          loadSectionThemesAssets(activeCanvas.value.sections, updateMultimediaNodeWithThemeAsset)
        }
      }
    } else {
      // Changed to false: Apply canvas theme to all components
      clearSectionThemes(activeCanvas.value.sections)

      // Only update theme assets if the feature is enabled
      if (areThemeAssetsEnabled.value) {
        updateComponentsWithThemeAssets(
          activeTheme.value,
          componentNodesById.value as Record<string, ComponentNode>,
          updateNodeDataById,
          activeCanvas.value,
        )
      }
    }
  } catch (e) {
    console.error('Error in handleMaintainSectionThemeChange:', e)

    // Fallback to basic functionality without theme assets
    if (value) {
      if (activeCanvas.value.sections?.length) {
        clearSectionThemes(activeCanvas.value.sections)
        loadSectionThemes(activeCanvas.value.sections)
      }
    } else {
      clearSectionThemes(activeCanvas.value.sections)
    }
  }
}

function onThemeSelect(theme: CanvasThemeRetrieve) {
  if (!theme) return

  // Apply the theme through the applyTheme function
  applyTheme(theme)

  // Reset revert state when selecting a new theme
  revertThemeId.value = null

  // Apply theme assets immediately
  immediatelyApplyThemeAssets(theme)
}

onMounted(async () => {
  if (launchDarkly.value.enable_themes) {
    fetchingThemes.value = true
    themes.value = (await themesFetcher?.())?.filter((theme) => theme.status === CanvasThemeStatus.READY)
    templateTheme.value = null

    if (isCanvas.value && activeCanvas.value?.template?.id) {
      try {
        templateTheme.value = (await canvasThemeFetcher?.({ canvas_id: activeCanvas.value.template.id })) ?? null
      } catch (e) {
        templateTheme.value = null
        console.error('Error fetching template theme:', e)
      }
    } else {
      templateTheme.value = themes.value?.find((theme) => theme.is_default) ?? null
    }
    fetchingThemes.value = false
  }

  if (customFonts.value?.length) {
    uploadedFonts.value = customFonts.value.map((item) => ({
      name: item.name ?? '',
      url: item.content_url,
    }))
  }

  // add the default font
  fontOptions.value.push({ label: DEFAULT_FONT, value: DEFAULT_FONT })

  // transform uploaded fonts to fontOptions data type
  uploadedFonts.value.forEach((item) => {
    fontOptions.value.push({ label: item.name, value: item.name, url: item.url })
  })

  // sort items in alphabetical order
  fontOptions.value = orderBy(fontOptions.value, ['label'])

  // add the '+ add font' item
  fontOptions.value.push({ label: t('canvasUI.canvasBuilder.canvasDrawerTheme.addFont'), value: 'add', action: 'add' })
})

function handleBackgroundPropertyChange(property: string, value: string | null) {
  switch (property) {
    case 'backgroundAttachment':
      backgroundAttachment.value = value as BackgroundAttachment
      break
    case 'backgroundImage':
      backgroundImage.value = value === null ? '' : value
      break
    case 'backgroundPositionX':
      backgroundPositionX.value = value as BackgroundPositionX
      break
    case 'backgroundPositionY':
      backgroundPositionY.value = value as BackgroundPositionY
      break
    case 'backgroundRepeat':
      backgroundRepeat.value = value as BackgroundRepeat
      break
    case 'backgroundSize':
      backgroundSize.value = value as BackgroundSize
      break
  }
}

defineExpose({
  onThemeSelect,
})
</script>
<style scoped>
.collapse-item :deep(.n-collapse-item__header-main) {
  padding: 0;
}
</style>
